/**
 * The scratch engine — the main-thread half of scratch-processor.js.
 *
 * Loads the worklet from a real module URL (never a Blob), pushes a decoded
 * buffer's channel data across the port, and exposes `rate` as an AudioParam so
 * the gesture layer can drive it with setTargetAtTime rather than a stream of
 * discrete assignments.
 */

export interface ScratchEngine {
  readonly node: AudioWorkletNode;
  readonly rate: AudioParam;
  load(buffer: AudioBuffer): void;
  start(offsetSeconds?: number): void;
  stop(): void;
  seek(seconds: number): void;
  setLoop(loop: boolean): void;
  /** Smoothed rate write — the only way the gesture layer should move it. */
  setRate(v: number, timeConstant?: number): void;
  /** Hard mute over ~15 ms — the cut gesture. */
  cut(): void;
  uncut(): void;
  onPlayhead(cb: (position: number) => void): void;
  readonly duration: number;
  setLevel(v: number): void;
  dispose(): void;
}

/**
 * Per-context, not module-global: a worklet module belongs to ONE
 * BaseAudioContext's registry, so a global cache would claim the module is
 * loaded on a context that has never seen it (any future second context —
 * e.g. a recovery path — would then throw InvalidStateError from the node
 * constructor). Rejections are evicted so a transient network failure is
 * retryable instead of poisoning the scene for the page's lifetime.
 */
const modulePromises = new WeakMap<BaseAudioContext, Promise<void>>();

export function ensureScratchModule(ctx: BaseAudioContext, url = 'worklets/scratch-processor.js'): Promise<void> {
  let p = modulePromises.get(ctx);
  if (!p) {
    p = ctx.audioWorklet.addModule(url).catch((err: unknown) => {
      modulePromises.delete(ctx);
      throw err;
    });
    modulePromises.set(ctx, p);
  }
  return p;
}

export async function createScratchEngine(
  ctx: AudioContext,
  out: AudioNode,
  level = 0.85,
  moduleUrl = 'worklets/scratch-processor.js',
): Promise<ScratchEngine> {
  await ensureScratchModule(ctx, moduleUrl);
  const node = new AudioWorkletNode(ctx, 'scratch-processor', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
  });
  const gate = ctx.createGain();
  gate.gain.value = 1;
  const master = ctx.createGain();
  master.gain.value = level * level;
  node.connect(gate); gate.connect(master); master.connect(out);

  const rate = node.parameters.get('rate')!;
  let duration = 0;
  let playheadCb: ((p: number) => void) | null = null;
  node.port.onmessage = (e) => {
    const m = e.data as { type: string; duration?: number; position?: number };
    if (m.type === 'loaded') duration = m.duration ?? 0;
    else if (m.type === 'playhead' && playheadCb) playheadCb(m.position ?? 0);
  };

  return {
    node,
    rate,
    get duration() { return duration; },
    load(buffer) {
      const channels: Float32Array[] = [];
      for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(buffer.getChannelData(c).slice());
      duration = buffer.duration;
      node.port.postMessage(
        { type: 'load', channels, sampleRate: buffer.sampleRate },
        channels.map((c) => c.buffer),
      );
    },
    start(offset = 0) { node.port.postMessage({ type: 'start', offset }); },
    stop() { node.port.postMessage({ type: 'stop' }); },
    seek(s) { node.port.postMessage({ type: 'seek', position: s }); },
    setLoop(loop) { node.port.postMessage({ type: 'loop', loop }); },
    setRate(v, tc = 0.012) { rate.setTargetAtTime(v, ctx.currentTime, tc); },
    cut() { gate.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.004); },
    uncut() { gate.gain.setTargetAtTime(1, ctx.currentTime, 0.01); },
    onPlayhead(cb) { playheadCb = cb; },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      // Tell the processor to end (its process() returns false on 'dispose'),
      // otherwise it is invoked forever on a disconnected graph. Then fade
      // before disconnect, same as every other engine.
      node.port.postMessage({ type: 'dispose' });
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02);
      setTimeout(() => { try { node.disconnect(); master.disconnect(); } catch { /* gone */ } }, 120);
    },
  };
}
